import { useLazyQuery, useMutation } from '@apollo/client'
import { StringHelpers } from '@dai/common'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'

import PricingTable from '@/components/account-set-up/steps/pricing/PricingTable'
import usePricingTable from '@/components/account-set-up/steps/pricing/usePricingTable'
import CenteredBox from '@/components/CenteredBox'
import {
  PlanType,
  PREVIEW_PLAN_CHANGE_INVOICE,
  PreviewPlanChangeInvoiceQuery,
  PreviewPlanChangeInvoiceQueryVariables,
  SubscriptionTypeFragment,
  UPDATE_SUBSCRIPTION_PLAN,
  UpdateSubscriptionPlanMutation,
  UpdateSubscriptionPlanMutationVariables,
} from '@/graphql'
import useActiveSubscription from '@/hooks/useActiveSubscription'
import useT from '@/i18n/useT'

function Pricing() {
  const navigate = useNavigate()

  const { t } = useT()

  const { isPremium, loading: activeSubscriptionLoading } =
    useActiveSubscription()

  const [error, setError] = useState<string | null>(null)
  const [previewPlanChangeInvoice, { data, loading: previewLoading }] =
    useLazyQuery<
      PreviewPlanChangeInvoiceQuery,
      PreviewPlanChangeInvoiceQueryVariables
    >(PREVIEW_PLAN_CHANGE_INVOICE, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    })

  const [selectedPlan, setSelectedPlan] =
    useState<SubscriptionTypeFragment | null>(null)

  const handleSelectPlan = (plan: SubscriptionTypeFragment) => {
    setSelectedPlan(plan)

    if (plan.prices[0]?.id) {
      previewPlanChangeInvoice({
        variables: {
          input: {
            priceId: plan.prices[0].id,
          },
        },
      }).catch(error => {
        setError(error.message)
      })
    }
  }

  const [
    updateSubscriptionPlan,
    { data: updateSubscriptionPlanData, loading: updatingSubscriptionPlan },
  ] = useMutation<
    UpdateSubscriptionPlanMutation,
    UpdateSubscriptionPlanMutationVariables
  >(UPDATE_SUBSCRIPTION_PLAN, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })

  const handleAcceptInvoice = (priceId: string) => {
    updateSubscriptionPlan({
      variables: {
        input: {
          priceId,
        },
      },
    }).catch(error => {
      setError(error.message)
    })
  }

  const {
    plans,
    loading,
    error: pricingError,
    checkoutInfo,
  } = usePricingTable()

  return (
    <>
      <Helmet title="Pricing" />
      <PricingTable
        handleSelectPlan={handleSelectPlan}
        selectedPlan={selectedPlan?.id}
        plans={plans}
        loading={loading}
        error={pricingError}
        checkoutInfo={checkoutInfo}
      />
      {!!selectedPlan?.prices[0]?.id && (
        <Dialog open={!!selectedPlan.prices[0].id} fullWidth>
          <DialogTitle>{t('pricingTable.invoiceTable.title')}</DialogTitle>
          <DialogContent>
            {previewLoading ||
            updatingSubscriptionPlan ||
            activeSubscriptionLoading ? (
              <CenteredBox sx={{ p: 4 }}>
                <CircularProgress />
              </CenteredBox>
            ) : error ? (
              <CenteredBox sx={{ p: 4 }}>
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              </CenteredBox>
            ) : updateSubscriptionPlanData?.updateSubscriptionPlan?.success ? (
              <CenteredBox sx={{ p: 4 }}>
                <Alert severity="success">
                  {t('pricingTable.invoiceTable.invoiceAccepted')}
                </Alert>
              </CenteredBox>
            ) : (
              data?.previewPlanChangeInvoice?.lines && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {selectedPlan.planType === PlanType.BASIC && isPremium
                            ? t(
                                'pricingTable.invoiceTable.endOfCycleDescription',
                              )
                            : t('pricingTable.invoiceTable.description')}
                        </TableCell>
                        <TableCell align="right">
                          {t('pricingTable.invoiceTable.amount')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.previewPlanChangeInvoice.lines.map(
                        (line, index) => (
                          <TableRow key={index}>
                            <TableCell>{line.description}</TableCell>
                            <TableCell align="right">
                              {StringHelpers.formatCurrency(line.amount)}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                      <TableRow sx={{ backgroundColor: 'background.default' }}>
                        <TableCell variant="head">
                          {t('pricingTable.invoiceTable.totalDueToday')}
                        </TableCell>
                        <TableCell align="right" variant="head">
                          {StringHelpers.formatCurrency(
                            data?.previewPlanChangeInvoice?.total || 0,
                          )}
                          <Typography variant="body1" color="textSecondary">
                            {t('pricingTable.invoiceTable.billedTo')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}
          </DialogContent>

          <DialogActions>
            {updateSubscriptionPlanData?.updateSubscriptionPlan?.success ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1)
                  } else {
                    navigate('/admin/settings/plan-management')
                  }
                }}
                disabled={updatingSubscriptionPlan}
              >
                {t('pricingTable.goBack')}
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setSelectedPlan(null)
                    setError(null)
                  }}
                  disabled={updatingSubscriptionPlan}
                >
                  {t('pricingTable.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || updatingSubscriptionPlan || !!error}
                  onClick={() => {
                    if (selectedPlan?.prices[0]?.id) {
                      handleAcceptInvoice(selectedPlan.prices[0].id)
                    }
                  }}
                >
                  {t('pricingTable.acceptInvoice')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default Pricing
